import { createAsyncThunk, createEntityAdapter, createSelector, createSlice, Dictionary } from '@reduxjs/toolkit';
import { JSONApiDataObject, ModalInfo, ReduxEntity } from 'Douleutaras/types';
import { makeRequest } from 'Douleutaras/services/apiClient';
import normalize from 'json-api-normalizer';
import { RootState } from 'Douleutaras/redux/store';

export const modalEndpoint = 'api/public/v3/modals/';
export const singleModalEndpoint = id => `${modalEndpoint}${id}/`;

const modalsAdapter = createEntityAdapter<JSONApiDataObject<ModalInfo>>();

export const getModalsSlugs = createAsyncThunk(
  'modals/fetch',
  async (additionalGetParams: Dictionary<string>, api) => {
    additionalGetParams = {
      'page[size]': '30',
      ...additionalGetParams
    };
    const response = await makeRequest(
      modalEndpoint,
      {
        method: 'GET',
        additionalGetParams
      }
    )(api);

    return normalize(response.data, { endpoint: modalEndpoint });
  }
);

export const getModalContent = createAsyncThunk(
  'modals/fetch/one',
  async (id: string, api) => {
    const response = await makeRequest(
      singleModalEndpoint(id),
      {
        method: 'GET'
      }
    )(api);

    return normalize(response.data, { endpoint: singleModalEndpoint(id) });
  }
);

export const modalsSlice = createSlice({
  name: 'modals',
  initialState: modalsAdapter.getInitialState({
    loading: false,
    error: false,
    meta: {},
    entities: {}
  } as ReduxEntity<JSONApiDataObject<ModalInfo>>),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(getModalsSlugs.pending, state => {
      state.loading = true;
    });
    builder.addCase(getModalsSlugs.fulfilled, (state, action) => {
      state.loading = false;
      modalsAdapter.upsertMany(state, action.payload.infoModal || {});
    });
    builder.addCase(getModalContent.fulfilled, (state, action) => {
      modalsAdapter.upsertMany(state, action.payload.infoModal || {});
    });
  }
});

export const modalSelectors = modalsAdapter.getSelectors<RootState>(state => state.modals);
export const selectModalBySlug = createSelector([
  state => state.modals,
  (state, { slug }: { slug: string }) => ({ slug })
], (modals, { slug }) => Object.values(modals.entities)
  .find(modal => (modal as ModalInfo).attributes.slug === slug)
);
